"use client";

import type { SupWeb3SelectedRoute } from "@/components/Web3/Common/Route/State";
import type { Web3TxMultiCalcSWR } from "@/hooks/tx-flow/useDepositCalculate";
import type { ResponseDepositStart } from "@/hooks/tx-flow/useDepositStart";
import type { SupTransactionType, SupWeb3Address, SupWeb3Balance, SupWeb3DepositStep, SupWeb3TxSingleVault, SupWithdrawableBalance, SupWithdrawableToken } from "@/types";
import React, { type Dispatch, Fragment, type SetStateAction, useCallback, useEffect, useMemo } from "react";

import useApiEnvState from "@/hooks/api-env/useApiEnvState";
import { twMerge } from "tailwind-merge";
import { SupX } from "@repo/core-ui";
import { SupButton, SupLoader, SupText } from "@repo/core-ui";

import { depositTransferError } from "@/utils";
import useRouterSimulation from "@/hooks/tx-flow/useRouterSimulation";
import useGetTotalTransactionFee from "@/hooks/web3/useGetTotalTransactionFee";

import Web3DebugCopyButton from "@/components/Web3/Debug/CopyButton";
import Web3CommonTransferTx from "@/components/Web3/Common/Transfer/Tx";

export interface Web3CommonTransferTxProps {
  vaultList: SupWeb3TxSingleVault[];
  selectedBalance: SupWeb3Balance | SupWithdrawableBalance | SupWithdrawableToken;
  disabled?: boolean;
  setStep: Dispatch<SetStateAction<SupWeb3DepositStep>>;
  setTransferTx: Dispatch<SetStateAction<string | undefined>>;
  setTotalGasCost: Dispatch<SetStateAction<number | undefined>>;
  setPauseRequests: Dispatch<SetStateAction<boolean>>;
  apiData: ResponseDepositStart | undefined;
  apiError: any | undefined;
  apiIsLoading: boolean;
  chainId: number;
  type?: SupTransactionType;
  calculatedRoutes: Web3TxMultiCalcSWR;
  selectedRoutes: SupWeb3SelectedRoute[];
}

interface Web3CommonTransferWrapperProps extends Web3CommonTransferTxProps {
  simulate: boolean;
  setSimulate: Dispatch<SetStateAction<boolean>>;
}

export default function Web3CommonTransferWrapper(props: Web3CommonTransferWrapperProps) {
  const [apiEnv] = useApiEnvState();

  const {
    chainId,
    vaultList,
    selectedRoutes,
    calculatedRoutes,
    simulate,
    setSimulate,
    apiData,
    setTotalGasCost,
    apiIsLoading,
    apiError,
    type = "deposit",
    setPauseRequests,
  } = props;

  useGetTotalTransactionFee({
    depositStartData: apiData,
    calculatedRoutes,
    setTotalGasCost,
  });

  const isSuperPool = useMemo(() => {
    return selectedRoutes?.some(single => single.selectedRoute !== "direct" && !!single.selectedRoute?.isERC20);
  }, [selectedRoutes]);

  const simulationsData = useMemo(
    () => ({
      chainId,
      txData: apiData?.data as SupWeb3Address,
      value: apiData?.value,
      isSuperPool,
      enabled: simulate,
    }),
    [chainId, apiData, isSuperPool, simulate]
  );

  const { data: routerSimulation, isError: isRouterSimulationError, error: routerSimulationError, isLoading: isRouterSimulationLoading } = useRouterSimulation(simulationsData);

  const transferEnabled = useMemo(() => (apiEnv === "staging" ? true : Boolean(routerSimulation?.success)), [apiEnv, routerSimulation?.success]);

  const handleSimulate = useCallback(() => {
    setSimulate(true);
  }, [setSimulate]);

  const isFailed = useMemo(() => {
    return (isRouterSimulationError && !isRouterSimulationLoading) || (apiError && !apiIsLoading) || (routerSimulation && !routerSimulation?.success && !isRouterSimulationLoading);
  }, [isRouterSimulationError, isRouterSimulationLoading, apiError, apiIsLoading, routerSimulation]);

  const isLoading = useMemo(() => {
    return isRouterSimulationLoading || apiIsLoading;
  }, [apiIsLoading, isRouterSimulationLoading]);

  useEffect(() => {
    setPauseRequests(isLoading);
  }, [isLoading, setPauseRequests]);

  const prefixIcon = useMemo(() => {
    return isFailed ? <SupX /> : isLoading ? <SupLoader isSmall color="primary" className="text-neutral-700" svgClassName="stroke-2" /> : undefined;
  }, [isFailed, isLoading]);

  const buttonText = useMemo(() => {
    if (apiError) {
      return "Can't simulate";
    }
    if (isFailed) {
      return "Simulation Failed";
    }
    if (isRouterSimulationLoading) {
      return "Simulating...";
    }
    if (apiIsLoading) {
      return "Loading...";
    }
    return transferEnabled ? "Re-simulate" : `Simulate ${type === "withdraw" ? "Withdrawal" : "Deposit"}`;
  }, [transferEnabled, type, apiError, isFailed, isRouterSimulationLoading, apiIsLoading]);

  const error = useMemo(() => {
    if (apiError) {
      return apiError;
    }
    if (routerSimulationError) {
      return routerSimulationError;
    }
    if (routerSimulation && !routerSimulation.success) {
      return new Error(routerSimulation.error_message);
    }

    return null;
  }, [apiError, routerSimulation, routerSimulationError]);

  return (
    <Fragment>
      {error && (
        <div className="bg-error-50 px-4 py-2">
          <SupText variant="subtitle2" className="text-error-925">
            {depositTransferError(error, chainId, vaultList)}
          </SupText>
          <Web3DebugCopyButton />
        </div>
      )}

      {transferEnabled && !error ? (
        <Web3CommonTransferTx {...props} />
      ) : (
        <SupButton
          prefixIcon={prefixIcon}
          disabled={isLoading || isFailed}
          className={twMerge("w-full justify-center", isFailed && "cursor-not-allowed", isLoading && "cursor-wait")}
          color={isFailed ? "error" : "primary"}
          onClickCapture={handleSimulate}
        >
          {buttonText}
        </SupButton>
      )}
    </Fragment>
  );
}
